import React from 'react';
import {
  Card,
  CardContent,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import MoreVert from '@mui/icons-material/MoreVert';
import { ItemCardProps, WorkFlowStatus } from './types';
import { useTranslation } from 'react-i18next';
import { EventBusy } from '@mui/icons-material';
import './WorkFlowCard.scss';
import { userRoles } from 'constants/constants';

export const WorkFlowCard: React.FC<ItemCardProps> = ({
  title,
  date,
  doer,
  role,
  status,
  inspector,
  projects,
  locations,
  areaOfObservation,
  className = '',
  onStatusClick,
  onDownload,
  onCancelOptionClick,
  onRescheduleOptionClick,
  onCardClick,
  onContinueDraft,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [open, setOpen] = React.useState(false);
  const getStatusClickHandler = (status: WorkFlowStatus) => () =>
    onStatusClick?.(status);

  const handleMoreClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  return (
    <Card className={`work-flow-card ${status?.toLowerCase()} ${className}`}>
      <CardContent className="d-flex h-100 item-details p-2 d-flex flex-column">
        <div className="i-header d-flex w-100 align-items-center">
          {date ? (
            <div className="date d-flex flex-grow-1">
              {status === WorkFlowStatus.CANCELED ? (
                <EventBusy className="date-icon" />
              ) : (
                <CalendarMonthOutlinedIcon className="date-icon" />
              )}

              <span className="date-field mx-1">{date}</span>
            </div>
          ) : (
            <div className="flex-grow-1" />
          )}
          <span
            onClick={getStatusClickHandler(status)}
            className={`status ${status?.toLowerCase()}`}
          >
            {status}
          </span>
          <IconButton
            hidden={status === WorkFlowStatus.DRAFT && role === userRoles.DOER}
            onClick={handleMoreClick}
          >
            <MoreVert />
          </IconButton>
        </div>

        <div className="d-flex flex-column flex-grow-1" onClick={onCardClick}>
          <span className="title">{title}</span>

          {locations?.length ? (
            <div className="d-flex my-1">
              <span>{t('label_locations')}: </span>
              <span className="locations mx-1">{locations?.join(' | ')}</span>
            </div>
          ) : null}

          {areaOfObservation && (
            <span className="locations">{`#${areaOfObservation}`}</span>
          )}

          {projects?.length ? (
            <div className="d-flex my-1">
              <span className="text-nowrap">{t('label_sub_projects')}: </span>
              <span className="projects mx-1">{projects?.join(' | ')}</span>
            </div>
          ) : null}

          <div className="d-flex flex-column flex-grow-1 justify-content-end">
            <Divider className="card-divider" />
            <div className="d-flex justify-content-between">
              <div className="d-flex">
                <span className="user">{t('label_doer')}: </span>
                <Tooltip title={doer ?? ''} placement="top" arrow>
                  <img
                    src={`https://ui-avatars.com/api/?size=24&color=fff&rounded=false&bold=true&background=random&name=${doer}`}
                    className="rounded-circle mx-1"
                    alt=""
                  />
                </Tooltip>
              </div>
              <div className="d-flex">
                <span className="user">{t('label_inspector')}:</span>
                <Tooltip title={inspector ?? ''} placement="top" arrow>
                  <img
                    src={`https://ui-avatars.com/api/?size=24&color=fff&rounded=false&bold=true&background=random&name=${inspector}`}
                    className="rounded-circle mx-1"
                    alt=""
                  />
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
      <Popover
        id="work-flow-popper"
        className="work-flow-popper"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <List className="workflow-list">
          {status !== WorkFlowStatus.SCHEDULED &&
            status !== WorkFlowStatus.DRAFT && (
              <ListItemButton>
                <ListItemText
                  primary={t('btn_download')}
                  onClick={() => {
                    handleClose();
                    onDownload?.();
                  }}
                />
              </ListItemButton>
            )}

          {status === WorkFlowStatus.DRAFT && role !== userRoles.DOER && (
            <ListItemButton>
              <ListItemText
                primary={t('btn_continue')}
                onClick={() => {
                  handleClose();
                  onContinueDraft?.();
                }}
              />
            </ListItemButton>
          )}

          {status === WorkFlowStatus.SCHEDULED && (
            <>
              <ListItemButton>
                <ListItemText
                  primary={t('btn_cancel')}
                  onClick={() => {
                    handleClose();
                    onCancelOptionClick?.();
                  }}
                />
              </ListItemButton>
              <ListItemButton>
                <ListItemText
                  primary={t('btn_reschedule')}
                  onClick={() => {
                    handleClose();
                    onRescheduleOptionClick?.();
                  }}
                />
              </ListItemButton>
            </>
          )}
        </List>
      </Popover>
    </Card>
  );
};
